export type AiUiLocale = 'he' | 'en';

export const AI_EMPTY_VALUE = '\u2014';
export const AI_HEBREW_CONTENT_UNAVAILABLE =
  'לא ניתן להציג תוכן זה בעברית.';

type TranslateFn = (
  key: string,
  options?: Record<string, unknown>
) => string;

type LocalizationIssue =
  | 'missing-hebrew-value'
  | 'missing-hebrew-translation'
  | 'blocked-raw-backend-message'
  | 'blocked-english-generated-content';

/**
 * The only locale resolver used by the AI module. Region variants and
 * i18next detector values are deliberately collapsed to the two locales
 * supported by the AI API/UI contract.
 */
export const normalizeUiLocale = (language?: string | null): AiUiLocale =>
  language?.trim().toLowerCase().startsWith('he') ? 'he' : 'en';

/**
 * Privacy-safe compliance telemetry. Never pass content, prompt text,
 * identifiers, or backend messages to this function.
 */
export const reportAiLocalizationIssue = (
  issue: LocalizationIssue,
  surface: string
): void => {
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[AI localization]', { issue, surface });
  }
};

export const strictTranslate = (
  t: TranslateFn,
  key: string,
  language: string,
  fallback = AI_EMPTY_VALUE
): string => {
  const locale = normalizeUiLocale(language);
  const translated = t(key, {
    lng: locale,
    fallbackLng: false,
    defaultValue: '',
  });

  if (translated && translated !== key) return translated;
  if (locale === 'he') {
    reportAiLocalizationIssue('missing-hebrew-translation', key);
  }
  return fallback;
};

export const resolveLocalizedValue = (
  value: Readonly<Record<string, string>> | undefined,
  language: string,
  surface = 'presentation'
): string => {
  if (!value) return AI_EMPTY_VALUE;
  const locale = normalizeUiLocale(language);
  const exact = value[locale]?.trim();
  if (exact) return exact;

  if (locale === 'he') {
    reportAiLocalizationIssue('missing-hebrew-value', surface);
    return AI_EMPTY_VALUE;
  }

  return value.en?.trim() || Object.values(value).find(Boolean)?.trim() || AI_EMPTY_VALUE;
};

const LATIN_TOKEN = /[A-Za-z][A-Za-z0-9_./:@+-]*/g;
const CONTEXTUAL_IDENTIFIER =
  /^(?:https?:\/\/\S+|[\w.+-]+@[\w.-]+\.[A-Za-z]{2,}|(?=.*[\d_./:@+-])[A-Za-z0-9_./:@+-]+|[A-Z]{2,}|Flow|[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)$/;

/**
 * Last-resort frontend gate for server-authored prose. Identifier-only
 * values (SKU/code/email/URL) are retained, while any mixed or all-Latin
 * user-facing prose is hidden in Hebrew UI. The server remains responsible
 * for context-aware proper-name validation.
 */
export const guardGeneratedText = (
  text: string,
  language: string,
  surface: string
): string => {
  if (normalizeUiLocale(language) !== 'he' || !text) return text;

  const trimmed = text.trim();
  if (CONTEXTUAL_IDENTIFIER.test(trimmed)) return text;

  const forbiddenTokens = (text.match(LATIN_TOKEN) ?? []).filter(
    (token) => !CONTEXTUAL_IDENTIFIER.test(token)
  );
  if (forbiddenTokens.length === 0) return text;

  reportAiLocalizationIssue('blocked-english-generated-content', surface);
  return AI_HEBREW_CONTENT_UNAVAILABLE;
};

export const resolveBackendNotice = (
  rawMessage: string | null | undefined,
  code: string | null | undefined,
  language: string,
  t: TranslateFn,
  fallbackKey: string
): string => {
  const locale = normalizeUiLocale(language);
  if (code === 'provider_unavailable') {
    return strictTranslate(t, 'ai.errors.provider-unavailable', locale);
  }
  if (locale === 'en' && rawMessage) return rawMessage;

  if (rawMessage) {
    reportAiLocalizationIssue('blocked-raw-backend-message', fallbackKey);
  }
  return strictTranslate(t, fallbackKey, locale);
};
